@file:OptIn(ExperimentalSerializationApi::class)

package common.frontend.api

import api.*
import api.DayOfWeek
import apps.App
import kotlinx.datetime.*
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlinx.serialization.protobuf.ProtoNumber
import org.ssoft.common.server.portal.PortalIO.EntryFilter
import org.ssoft.common.server.portal.PortalIO.ImplServer
import org.ssoft.common.tools.*
import org.ssoft.common.type.AspectRatio
import org.ssoft.common.type.DashboardType
import org.ssoft.common.type.ResolutionStandard
import org.ssoft.common.type.ScreenRatio
import playlist.ImplPlaylist
import tk.mallumo.utils.GenId
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds

object Io {

    @Deprecated(
        message = "do not use",
        replaceWith = ReplaceWith("org.ssoft.common.server.portal.UPermission")
    )
    annotation class UPermission

    @Deprecated("scheduled to remove")
    @Serializable
    data class DeviceRegistration(
        @ProtoNumber(1)
        var name: String,
        @ProtoNumber(2)
        var key: String
    ) {
        companion object;
    }

    @Serializable
    data class Branding(
        @ProtoNumber(1)
        var colors: Colors,
        @ProtoNumber(2)
        var images: MutableList<Long> = mutableListOf(),
        @ProtoNumber(3)
        var logo: Long,
        @ProtoNumber(4)
        var name: String,
        @ProtoNumber(5)
        var text: Text,
        @ProtoNumber(6)
        var timeout: Timeout,
        @ProtoNumber(7)
        var overlay: Overlay,
        @ProtoNumber(8)
        var sizes: Sizes = Sizes(),
        @ProtoNumber(9)
        var params: Params = Params(),
        @ProtoNumber(10)
        var layout: Layout = Layout()
    ) {
        companion object;

        @Serializable
        data class Params(
            @ProtoNumber(1)
            var slideshowCropImages: Boolean = true
        )

        @Serializable
        data class Layout(
            @ProtoNumber(1)
            /**
             * @see KioskLayoutType
             */
            var type: Int = KioskLayoutType.STANDARD.mask
        )

        @Serializable
        data class Overlay(
            @ProtoNumber(1)
            var land: Long,
            @ProtoNumber(2)
            var port: Long,
        )

        @Serializable
        data class Text(
            @ProtoNumber(1)
            var closeWarningTitle: String,
            @ProtoNumber(2)
            var closeWarningDesc: String,
            @ProtoNumber(3)
            var closeWarningAction: String,
        )

        @Serializable
        data class Timeout(
            @ProtoNumber(1)
            var handicap: Long,
            @ProtoNumber(2)
            var dialog: Long,
            @ProtoNumber(3)
            var screen: Long,
        )

        @Serializable
        data class Sizes(
            @ProtoNumber(1)
            var dashboardIconScale: Int = 60
        )

        @Serializable
        data class Colors(
            @ProtoNumber(1)
            var background: Long,
            @ProtoNumber(2)
            var onBackground: Long,

            @ProtoNumber(3)
            var primary: Long,
            @ProtoNumber(4)
            var onPrimary: Long,

            @ProtoNumber(5)
            var secondary: Long,
            @ProtoNumber(6)
            var onSecondary: Long,

            @ProtoNumber(7)
            var primaryContainer: Long,
            @ProtoNumber(8)
            var onPrimaryContainer: Long,

            @ProtoNumber(9)
            var secondaryContainer: Long,
            @ProtoNumber(10)
            var onSecondaryContainer: Long,
        )
    }

    @Serializable
    data class BundleCompanyWithLocation(
        @ProtoNumber(1)
        var items: List<Company> = emptyList()
    ) {
        @Serializable
        data class Company(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var location: List<Location> = emptyList(),
        ) : EntryFilter

        @Serializable
        data class Location(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String
        ) : EntryFilter
    }

    @Serializable
    data class ListingDomain(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var title: String = "ScreenSoft",
            @ProtoNumber(4)
            var ico: Long = ImplServer.DEFAULT_IMAGE_ICO,
            @ProtoNumber(5)
            var urls: Urls = Urls(),
        ) : EntryFilter

        @Serializable
        data class Urls(
            @ProtoNumber(1)
            var home: String = "screensoft.org",
            @ProtoNumber(2)
            var control: String = "my.screensoft.org",
            @ProtoNumber(3)
            var game: String = "game.screensoft.org",
        )
    }

    @Serializable
    data class ListingCompany(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var count_user: Int,
            @ProtoNumber(4)
            var count_device: Int,
            @ProtoNumber(5)
            var count_location: Int
        ) : EntryFilter {
            companion object {
                val EMPTY: Item
                    get() = Item(
                        id = -1L,
                        name = "",
                        count_user = 0,
                        count_device = 0,
                        count_location = 0
                    )
            }
        }
    }

    @Serializable
    data class ListingRssChannel(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String = "",
            @ProtoNumber(3)
            var url: String,
            @ProtoNumber(4)
            var dtLast: LocalDateTime,
            @ProtoNumber(5)
            var intervalMs: Long
        ) : EntryFilter {
            companion object {
                val EMPTY = Item(
                    id = -1L,
                    name = "",
                    url = "",
                    dtLast = LocalDateTime.EMPTY,
                    intervalMs = 6.hours.inWholeMilliseconds,
                )
            }
        }
    }

    @Serializable
    data class ListingRssInstance(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var nameChannel: String,
            @ProtoNumber(4)
            var usageDevice: Int,
            @ProtoNumber(5)
            var usageCompany: Int
        ) : EntryFilter
    }

    @Serializable
    data class ListingAppManager(
        @ProtoNumber(1)
        var companyArr: MutableList<ItemCompany> = mutableListOf(),
        @ProtoNumber(2)
        var variantArr: MutableList<ItemAppVariant> = mutableListOf()
    ) {
        @Serializable
        data class ItemCompany(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var variants: MutableList<AppVariantEntry> = mutableListOf()
        ) : EntryFilter {
            @Serializable
            data class AppVariantEntry(
                @ProtoNumber(1)
                override var id: Long,
                @ProtoNumber(2)
                override var name: String = "",
                @ProtoNumber(3)
                var type: Int,
                @ProtoNumber(4)
                var enabled: Boolean
            ) : EntryFilter
        }

        @Serializable
        data class ItemAppVariant(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var logo: Long,
            @ProtoNumber(4)
            var type: Int,
        ) : EntryFilter
    }

    @Serializable
    data class ListingAppVariant(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var type: Int,
            @ProtoNumber(4)
            var description: String,
            @ProtoNumber(5)
            var logo: Long,
        ) : EntryFilter
    }

    @Serializable
    data class ListingApp(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Device(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String
        )

        @Serializable
        data class Campaign(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String
        )

        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            var appVariantId: Long,
            @ProtoNumber(3)
            override var name: String,
            @ProtoNumber(4)
            var type: Int,
            @ProtoNumber(5)
            var note: String,
            @ProtoNumber(6)
            var logo: Long,
            @ProtoNumber(7)
            var start: LocalDate,
            @ProtoNumber(9)
            var end: LocalDate,
            @ProtoNumber(10)
            var scheduled: Boolean,
            @ProtoNumber(11)
            var position: Int,
            @ProtoNumber(12)
            var connection: List<AppConnection> = listOf(),
            @ProtoNumber(13)
            var devices: List<Device> = listOf(),
            @ProtoNumber(14)
            var campaigns: List<Campaign> = listOf(),
            @ProtoNumber(15)
            var archived: Boolean = false,
        ) : EntryFilter

    }

    @Serializable
    data class AppConnection(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var logo: Long,
        @ProtoNumber(3)
        var name: String
    )

    @Serializable
    data class AppDelete(
        @ProtoNumber(1)
        val companyId: Long,
        @ProtoNumber(2)
        val appId: Long,
        @ProtoNumber(3)
        val keepLogo: Boolean,
        @ProtoNumber(4)
        val keepContent: Boolean
    )

    @Serializable
    data class AppDetail(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(3)
        var name: String,
        @ProtoNumber(4)
        var type: Int,
        @ProtoNumber(5)
        var note: String,
        @ProtoNumber(6)
        var logo: Long,
        @ProtoNumber(7)
        var locations: MutableList<LocationItem> = mutableListOf(),
        @ProtoNumber(8)
        var data: App,
        @ProtoNumber(9)
        var start: LocalDate,
        @ProtoNumber(10)
        var end: LocalDate,
        @ProtoNumber(11)
        var scheduled: Boolean,
        @ProtoNumber(12)
        var variant: Variant,
        @ProtoNumber(13)
        var nodes: MutableList<AppNode> = mutableListOf(),
        @ProtoNumber(14)
        var connection: List<AppConnection> = listOf(),
        @ProtoNumber(15)
        var campaigns: List<CampaignNode> = listOf(),
        @ProtoNumber(16)
        var archived: Boolean = false
    ) {

        val qrUrl: String get() = "https://game.screensoft.org/${type}/${id}"

        @Serializable
        data class CampaignNode(
            var id: Long = -1L,
            var name: String = ""
        )

        @Serializable
        data class AppNode(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var logo: Long,
            @ProtoNumber(3)
            var name: String,
            @ProtoNumber(4)
            var position: Int,
            @ProtoNumber(5)
            var active: Boolean
        )

        @Serializable
        data class Variant(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String,
        )

        @Serializable
        data class LocationItem(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var devices: MutableList<DeviceItem> = mutableListOf()
        ) : EntryFilter {
            companion object;
        }

        @Serializable
        data class DeviceItem(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var enabled: Boolean,
            @ProtoNumber(4)
            var width: Int = 0,
            @ProtoNumber(5)
            var height: Int = 0
        ) : EntryFilter {
            companion object;
        }
    }

    @Serializable
    data class ListingServer(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {

        @Serializable
        data class License(
            @ProtoNumber(1)
            var percentage: Int = 100,
            /**
             * @see api.CurrencyType
             */
            @ProtoNumber(2)
            var currency: Int = CurrencyType.UNDEFINED.mask,
            @ProtoNumber(3)
            var sum: Int = 0
        )

        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var count_user: Int,
            @ProtoNumber(4)
            var count_device: Int,
            @ProtoNumber(5)
            var count_location: Int,
            @ProtoNumber(6)
            var count_company: Int,
            @ProtoNumber(7)
            var license: License = License()
        ) : EntryFilter {
            companion object {
                val EMPTY: Item
                    get() = Item(
                        id = -1L,
                        name = "",
                        count_user = 0,
                        count_device = 0,
                        count_location = 0,
                        count_company = 0
                    )
            }
        }
    }

    @Serializable
    data class ListingHardware(
        @ProtoNumber(1)
        var items: MutableList<Hardware> = mutableListOf()
    ) {

        @Serializable
        data class Hardware(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var image: Long,
            @ProtoNumber(4)
            var type: Int,
            @ProtoNumber(5)
            var capability: Int
        ) : EntryFilter
    }

    @Serializable
    data class ListingCampaignDeviceDoyCheck(
        @ProtoNumber(1)
        var items: MutableList<Entry> = mutableListOf(),
    ) {
        @Serializable
        data class Entry(
            @ProtoNumber(1)
            var campaignId: Long = -1,
            @ProtoNumber(2)
            var campaignName: String = "",
            @ProtoNumber(3)
            var visual: Long = 0
        )
    }

    @Serializable
    data class ListingCampaignDevicesCheck(
        @ProtoNumber(1)
        var items: MutableList<Entry> = mutableListOf(),
    ) {
        @Serializable
        data class Entry(
            @ProtoNumber(1)
            var deviceId: Long = -1,
            @ProtoNumber(2)
            var doy: Long = -1,
            @ProtoNumber(3)
            var sum: Long = 0
        )
    }

    @Serializable
    data class ListingUser(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var name_company: String,
            @ProtoNumber(4)
            var loginDt: LocalDateTime
        ) : EntryFilter {
            companion object {
                val EMPTY: Item
                    get() = Item(
                        id = -1L,
                        name = "",
                        name_company = "",
                        loginDt = kotlin.time.Clock.nowDateTime()
                    )
            }
        }
    }

    @Serializable
    data class ListingTranslation(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var transeable: Boolean,
            @ProtoNumber(4)
            var variants: Map<String, Entry>
        ) : EntryFilter {
            companion object;
        }

        @Serializable
        data class Entry(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var value: String,
            @ProtoNumber(3)
            var translated: Boolean,
            @ProtoNumber(4)
            var lang: String,
            @ProtoNumber(5)
            var change: Boolean = false
        )
    }

    @Serializable
    data class ListingLocation(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            var id_company: Long,
            @ProtoNumber(3)
            override var name: String,
            @ProtoNumber(4)
            var name_company: String,
            @ProtoNumber(5)
            var count_device: Int,
            @ProtoNumber(6)
            var emergencyActive: Boolean,
        ) : EntryFilter {
            companion object {
                val EMPTY: Item
                    get() = Item(
                        id = -1L,
                        id_company = -1L,
                        name = "",
                        name_company = "",
                        count_device = 0,
                        emergencyActive = false
                    )
            }
        }
    }

    @Serializable
    data class ListingDevice(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var name_company: String,
            @ProtoNumber(4)
            var name_location: String,
            @ProtoNumber(5)
            var layer: Layer,
            @ProtoNumber(6)
            var isPlaylistRandomOrder: Boolean,
            @ProtoNumber(7)
            var companyId: Long,
            @ProtoNumber(8)
            var locationId: Long,
            @ProtoNumber(9)
            var connectivity: Connectivity = Connectivity(),
            @ProtoNumber(10)
            var apps: List<ClientAppVersion> = listOf(),
            @ProtoNumber(11)
            var photo: Photo,
            @ProtoNumber(12)
            var emergencyActive: Boolean,
            @ProtoNumber(13)
            var campaignInfo: CampaignInfo = CampaignInfo()
        ) : EntryFilter {
            companion object;

            @Serializable
            data class Photo(
                @ProtoNumber(1)
                var resourceId: Long,
                @ProtoNumber(2)
                var width: Int,
                @ProtoNumber(3)
                var height: Int
            )

            @Serializable
            data class CampaignInfo(
                @ProtoNumber(1)
                var countImpressionsToday: Int = 0
            )
        }

        @Serializable
        data class Connectivity(
            @ProtoNumber(1)
            var online: Boolean = false,
            @ProtoNumber(2)
            var change: Duration = 0.milliseconds
        )

        @Serializable
        data class Layer(
            @ProtoNumber(1)
            var top: Boolean,
            @ProtoNumber(2)
            var bottom: Boolean,
            @ProtoNumber(3)
            var topCount: Int = 0,
            @ProtoNumber(4)
            var bottomCount: Int = 0,
        )
    }

    @Serializable
    data class ListingCampaign(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf(),
        @ProtoNumber(2)
        var canCreate: Boolean,
        @ProtoNumber(3)
        var companySettings: CompanySettings,
        @ProtoNumber(4)
        var counter: Counter,
    ) {

        @Serializable
        data class Counter(
            @ProtoNumber(1)
            var devices: Devices
        ) {

            @Serializable
            data class Devices(
                @ProtoNumber(1)
                var all: Int,
                @ProtoNumber(2)
                var withTopLayer: Int
            )
        }

        @Serializable
        data class CompanySettings(
            @ProtoNumber(1)
            /** @see api.CampaignType */
            var type: Int,
            @ProtoNumber(2)
            var itemCount: Int,
            @ProtoNumber(3)
            var itemDuration: Long,
            @ProtoNumber(4)
            var enableCommercial: Boolean,
            @ProtoNumber(5)
            var enableCommercialAsNonCommercial: Boolean,
        ) {
            companion object;
        }

        @Serializable
        data class CampaignSettings(
            @ProtoNumber(1)
            /** @see api.CampaignType */
            var type: Int,
            @ProtoNumber(2)
            var itemCount: Int,
            @ProtoNumber(3)
            var itemDuration: Long,
        ) {
            companion object;
        }

        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(14)
            var groupId: Long,
            @ProtoNumber(2)
            var companyId: Long,
            @ProtoNumber(3)
            override var name: String,
            @ProtoNumber(4)
            var state: Int,
            @ProtoNumber(5)
            var start: LocalDateTime,
            @ProtoNumber(6)
            var end: LocalDateTime,
            @ProtoNumber(7)
            var devices: Int,
            @ProtoNumber(8)
            var visuals: List<VisualInfo> = listOf(),
            @ProtoNumber(9)
            var isCommercial: Boolean,
            @ProtoNumber(10)
            var isNonCommercial: Boolean,
            @ProtoNumber(11)
            var campaignSetting: CampaignSettings,
            @ProtoNumber(12)
            var impressionExpected: Long,
            @ProtoNumber(13)
            var impressionsSum: Int = 0,
            @ProtoNumber(15)
            var editorVersion: Int = 1
        ) : EntryFilter {

            companion object {
                fun empty(
                    companyId: Long,
                    isNonCommercial: Boolean,
                    campaignSetting: CampaignSettings
                ): Item {
                    val now = kotlin.time.Clock.nowDateTime()
                    return Item(
                        id = -1L,
                        groupId = -1L,
                        companyId = companyId,
                        name = "",
                        state = CampaignState.DRAFT.state,
                        start = now,
                        end = (now.date.plus(1, DateTimeUnit.MONTH) + LocalTime.MAX),
                        devices = 0,
                        visuals = listOf(),
                        isCommercial = false,
                        isNonCommercial = isNonCommercial,
                        campaignSetting = campaignSetting,
                        impressionExpected = 0L
                    )
                }
            }

            @Serializable
            data class VisualInfo(
                @ProtoNumber(1)
                var id: Long,
                @ProtoNumber(2)
                var width: Int,
                @ProtoNumber(3)
                var height: Int
            )
        }
    }

    @Serializable
    data class ListingPlaylistOrder(
        @ProtoNumber(1)
        var items: List<Item> = listOf()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(1)
            var campaignId: Long,
            @ProtoNumber(2)
            var position: Int
        )
    }

    @Serializable
    data class ListingResource(
        @ProtoNumber(1)
        var items: MutableList<Item> = mutableListOf(),
        @ProtoNumber(2)
        var space: Space = Space()
    ) {

        @Serializable
        data class Space(
            @ProtoNumber(1)
            var active: Long = 0,
            @ProtoNumber(2)
            var total: Long = 0,
            @ProtoNumber(3)
            var trash: Long = 0,
            @ProtoNumber(4)
            var unused: Long = 0,
        ) {
            companion object {
                const val LIMIT_SPACE = 10_000_000_000L
            }
        }

        @Serializable
        data class Item(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            /** @see api.ResourceType */
            @Suppress("KDocUnresolvedReference") @ProtoNumber(3)
            var type: Int,
            @ProtoNumber(4)
            var width: Int,
            @ProtoNumber(5)
            var height: Int,
            @ProtoNumber(6)
            var duration: Long,
            /** @see org.ssoft.common.type.AspectRatio */
            @ProtoNumber(7)
            var aspectRatio: Int,
        ) : EntryFilter {
            companion object;
        }
    }


    @Serializable
    data class Hardware(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var name: String,
        @ProtoNumber(3)
        var image: Long,
        @ProtoNumber(4)
        var type: Int,
        @ProtoNumber(5)
        var translations: List<Translation> = listOf(),
        @ProtoNumber(6)
        var capability: Int
    ) {

        companion object {
            val EMPTY_COMPUTER: Hardware
                get() = Hardware(
                    id = -1L,
                    name = "",
                    image = -1L,
                    type = HardwareType.COMPUTER.type,
                    capability = HardwareCapability.UNDEFINED.mask,
                    translations = Translation.ALL
                )
        }


        @Serializable
        data class Translation(
            @ProtoNumber(1)
            var lang: String,
            @ProtoNumber(2)
            var subtitle: String = "",
            @ProtoNumber(3)
            var description: String = "",
        ) {
            companion object {
                val ALL
                    get() = LanguageType.all.map {
                        Translation(
                            lang = it.type,
                            subtitle = "",
                            description = ""
                        )
                    }
            }
        }
    }

    @Serializable
    data class Resource(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var name: String,
        /** @see api.ResourceType */
        @Suppress("KDocUnresolvedReference") @ProtoNumber(3)
        var type: Int,
        @ProtoNumber(4)
        var width: Int,
        @ProtoNumber(5)
        var pages: Int,
        @ProtoNumber(6)
        var size: Long,
        @ProtoNumber(7)
        var height: Int,
        @ProtoNumber(8)
        var duration: Long,
        /** @see org.ssoft.common.type.AspectRatio */
        @ProtoNumber(9)
        var aspectRatio: Int,
        @ProtoNumber(10)
        var dtCreate: LocalDateTime,
        @ProtoNumber(11)
        var dtReady: LocalDateTime,
        @ProtoNumber(12)
        var conversionError: Boolean,
        @ProtoNumber(13)
        var usage: List<Usage> = listOf(),
        @ProtoNumber(14)
        var withoutConversion: Boolean
    ) {


        companion object;
        @Serializable
        data class Usage(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String,
            @ProtoNumber(3)
            var dt: LocalDateTime,
        )
    }

    @Serializable
    data class ClientAppVersion(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var version: Long,
        @ProtoNumber(3)
        var dt: LocalDateTime,
        @ProtoNumber(4)
        var lastStable: Long = -1,
    )

    @Deprecated("do not use")
    @Serializable
    data class Device(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        override var name: String,
        @ProtoNumber(3)
        override var mac: String,
        @ProtoNumber(5)
        override var config: ImplDevice.Config,
        @ProtoNumber(8)
        override var photoResId: Long,
        @ProtoNumber(10)
        override var address: ImplDevice.Address,

        @ProtoNumber(4)
        var location: Location,
        @ProtoNumber(7)
        var apps: List<ClientAppVersion> = listOf(),
        @ProtoNumber(9)
        var campaignsCount: Int,
        @ProtoNumber(11)
        var emergency: ImplDevice.Emergency,
        @ProtoNumber(12)
        var hardware: ImplDevice.HardwareVariant
    ) : ImplDevice {

        @Serializable
        data class Location(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            override var company: CompanyBase,
            @ProtoNumber(4)
            override var count_device: Int = 0,
            @ProtoNumber(5)
            override var days: MutableList<ImplLocation.Day> = ImplLocation.Day.emptyWeek(),
            @ProtoNumber(6)
            override var main: Int
        ) : EntryFilter, ImplLocation {
            companion object;
        }
    }

    @Serializable
    data class DeviceEditor(
        @ProtoNumber(1)
        override var id: Long = -1L,
        @ProtoNumber(2)
        override var name: String = "",
        @ProtoNumber(3)
        override var mac: String = "",
        @ProtoNumber(4)
        override var config: ImplDevice.Config = ImplDevice.Config(),
        @ProtoNumber(5)
        override var photoResId: Long = -1,
        @ProtoNumber(6)
        override var address: ImplDevice.Address = ImplDevice.Address(),
        @ProtoNumber(7)
        var locationId: Long = -1L,
        @ProtoNumber(8)
        var companyId: Long = -1L,
        @ProtoNumber(9)
        var hardwareId: Long = 1029,
        @ProtoNumber(10)
        var attachedCampaign: Boolean = false,
        @ProtoNumber(11)
        var hardware: List<ImplDevice.HardwareVariant> = listOf(),
        @ProtoNumber(12)
        var companyBundle: BundleCompanyWithLocation = BundleCompanyWithLocation(),
        @ProtoNumber(13)
        var defaultKioskAppId: Long = -1L,
        @ProtoNumber(14)
        var apps: List<App> = emptyList(),
    ) : ImplDevice {
        @Serializable
        data class App(
            @ProtoNumber(1)
            var id: Long = -1L,
            @ProtoNumber(2)
            var name: String = "",
            @ProtoNumber(3)
            var logo: Long = -1L,
            /**
             * @see AppType
             */
            @ProtoNumber(4)
            var type: Int = AppType.NONE.type,
        )
    }

    @Serializable
    data class DeviceInfo(
        @ProtoNumber(1)
        override var id: Long = -1L,
        @ProtoNumber(2)
        override var name: String = "",
        @ProtoNumber(3)
        override var mac: String = "",
        @ProtoNumber(4)
        override var config: ImplDevice.Config = ImplDevice.Config(),
        @ProtoNumber(5)
        override var photoResId: Long = -1,
        @ProtoNumber(6)
        override var address: ImplDevice.Address = ImplDevice.Address(),
        @ProtoNumber(7)
        var hardware: ImplDevice.HardwareVariant? = null,
        @ProtoNumber(8)
        var location: NameId = NameId(),
        @ProtoNumber(9)
        var company: NameId = NameId(),
        @ProtoNumber(10)
        var apps: List<ClientAppVersion> = listOf(),
        @ProtoNumber(11)
        var emergency: ImplDevice.Emergency,
        @ProtoNumber(12)
        var kioskDefault: KioskDefault = KioskDefault()
    ) : ImplDevice {
        @Serializable
        data class KioskDefault(
            @ProtoNumber(1)
            override var id: Long = -1L,
            @ProtoNumber(2)
            override var name: String = "",
            @ProtoNumber(3)
            var logo: Long = -1L,
        ) : EntryFilter
    }

    @Serializable
    data class NameId(
        @ProtoNumber(1)
        override var id: Long = -1L,
        @ProtoNumber(2)
        override var name: String = ""
    ) : EntryFilter


    interface ImplDevice : EntryFilter {
        override var id: Long
        override var name: String
        var mac: String
        var config: Config
        var photoResId: Long
        var address: Address

        @Serializable
        data class Emergency(
            @ProtoNumber(1)
            var image: Long,
            @ProtoNumber(2)
            var active: Boolean,
        )

        @Serializable
        data class Config(
            @ProtoNumber(1)
            var display: Display = Display(),
            @ProtoNumber(2)
            var layer: Layer = Layer(),
            @ProtoNumber(3)
            var volume: Int = 50,
            @ProtoNumber(4)
            var touch: Touch = Touch(),
            @ProtoNumber(5)
            var cursorVisible: Boolean = false
        )

        @Serializable
        data class Address(
            @ProtoNumber(1)
            var lat: Double = 0.0,
            @ProtoNumber(2)
            var lon: Double = 0.0
        )

        @Serializable
        data class Display(
            @ProtoNumber(1)
            var rotation: Int = 0,
            @ProtoNumber(2)
            var width: Int = 1920,
            @ProtoNumber(3)
            var height: Int = 1080,
            @ProtoNumber(4)
            var screenRatio: Int = ScreenRatio._16_9.mask,
            @ProtoNumber(5)
            var videoScale: Int = ResolutionStandard.FHD.mask
        )

        @Serializable
        data class Layer(
            @ProtoNumber(1)
            var top: Boolean = true,
            @ProtoNumber(2)
            var bottom: Boolean = true,
        )

        @Serializable
        data class Touch(
            @ProtoNumber(1)
            var rotation: Int = 0
        )

        @Serializable
        data class HardwareVariant(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var image: Long,
            @ProtoNumber(3)
            var name: String,
            @ProtoNumber(4)
            var subtitle: String,
        )
    }

    @Serializable
    data class Company(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        override var name: String,
        @ProtoNumber(3)
        var count_user: Int,
        @ProtoNumber(4)
        var count_device: Int,
        @ProtoNumber(5)
        var count_location: Int,
        @ProtoNumber(6)
        var main: Int,
        @ProtoNumber(7)
        var campaign: Campaign,
        @ProtoNumber(8)
        val defaults: Defaults
    ) : EntryFilter {
        companion object;

        @Serializable
        data class Defaults(
            @ProtoNumber(1)
            var visual: Visual,
        )

        @Serializable
        data class Visual(
            @ProtoNumber(1)
            var land: Long,
            @ProtoNumber(2)
            var port: Long
        )

        @Serializable
        data class Campaign(
            /** @see api.CampaignType */
            @ProtoNumber(1)
            var type: Int,
            @ProtoNumber(2)
            var canChange: Boolean,
            @ProtoNumber(3)
            var itemCount: Int,
            @ProtoNumber(4)
            var itemDuration: Long,
            @ProtoNumber(5)
            var enableCommercial: Boolean,
            @ProtoNumber(6)
            var enableCommercialAsNonCommercial: Boolean,
            @ProtoNumber(7)
            var resources: Resources
        )

        @Serializable
        data class Resources(
            @ProtoNumber(1)
            var ignoreAspectRatio: Boolean,
            @ProtoNumber(2)
            var ignoreDuration: Boolean,
            @Deprecated("do not use")
            @ProtoNumber(3)
            var ignoreRotation: Boolean
        )
    }

    @Serializable
    data class ServerInfo(
        @ProtoNumber(1)
        override var imageLogoHorizontal: Long = -29L,
        @ProtoNumber(2)
        override var imageLogoSquare: Long = -30L,
        /** White-label documentation web (`SERVER.docs_url`), may be without scheme */
        @ProtoNumber(3)
        var docsUrl: String = "docs.screensoft.org",
    ) : ImplServer

    @Serializable
    data class Server(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        override var name: String,
        @ProtoNumber(3)
        var editLocked: Boolean,


        @ProtoNumber(4)
        var companyName: String,
        @ProtoNumber(5)
        var companyAddr: String,
        @ProtoNumber(6)
        var companyVat: String,
        @ProtoNumber(7)
        var companyId: String,

        @ProtoNumber(8)
        var responsibleName: String,
        @ProtoNumber(9)
        var responsibleTel: String,
        @ProtoNumber(10)
        var responsibleEmail: String,
        @ProtoNumber(11)
        var responsibleWww: String,


        @ProtoNumber(12)
        var pricePct: Int,
        @ProtoNumber(13)
        var priceCurrencyType: Int,
        @ProtoNumber(14)
        var pricePerson: String,
        @ProtoNumber(15)
        var priceMail: String,

        @ProtoNumber(16)
        var countUser: Int,
        @ProtoNumber(17)
        var countDevice: Int,
        @ProtoNumber(18)
        var countLocation: Int,
        @ProtoNumber(19)
        var countCompany: Int,

        @ProtoNumber(20)
        var imageIco: Long,
        @ProtoNumber(21)
        override var imageLogoHorizontal: Long,
        @ProtoNumber(22)
        override var imageLogoSquare: Long,
        @ProtoNumber(23)
        var domainUrl: String,
        @ProtoNumber(24)
        var domainTitle: String,
        @ProtoNumber(25)
        var homeUrl: String,
        @ProtoNumber(26)
        var gameUrl: String,
        @ProtoNumber(27)
        var docsUrl: String,
        @ProtoNumber(28)
        var imageLogoDocsLight: Long,
        @ProtoNumber(29)
        var imageLogoDocsDark: Long,
    ) : EntryFilter, ImplServer {
        companion object;
    }

    @Deprecated(
        message = "do not use",
        replaceWith = ReplaceWith("org.ssoft.common.server.portal.CompanyBase")
    )
    @Serializable
    data class CompanyBase(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        override var name: String
    ) : EntryFilter {
        companion object;
    }

    interface ImplLocation : EntryFilter {
        override var id: Long
        override var name: String
        var company: CompanyBase
        var count_device: Int
        var days: MutableList<Day>
        var main: Int

        companion object;

        @Serializable
        data class Day(
            @ProtoNumber(1)
            var day: Int,
            @ProtoNumber(2)
            var enabled: Int,
            @ProtoNumber(3)
            var start: LocalTime,
            @ProtoNumber(4)
            var end: LocalTime,
        ) {

            val name: String
                get() = DayOfWeek[day].label

            companion object {
                fun empty(day: DayOfWeek) = Day(
                    day = day.index,
                    enabled = 1,
                    start = LocalTime(6, 0),
                    end = LocalTime(22, 0),
                )

                fun emptyWeek() = mutableListOf(
                    empty(DayOfWeek.MONDAY),
                    empty(DayOfWeek.TUESDAY),
                    empty(DayOfWeek.WEDNESDAY),
                    empty(DayOfWeek.THURSDAY),
                    empty(DayOfWeek.FRIDAY),
                    empty(DayOfWeek.SATURDAY),
                    empty(DayOfWeek.SUNDAY),
                )
            }
        }
    }

    @Serializable
    data class Location(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        override var name: String,
        @ProtoNumber(3)
        override var company: CompanyBase,
        @ProtoNumber(4)
        override var count_device: Int = 0,
        @ProtoNumber(5)
        override var days: MutableList<ImplLocation.Day> = ImplLocation.Day.emptyWeek(),
        @ProtoNumber(6)
        override var main: Int,
        @ProtoNumber(7)
        var branding: Branding,
        @ProtoNumber(8)
        var emergency: Emergency,
    ) : EntryFilter, ImplLocation {

        companion object;

        @Serializable
        data class Emergency(
            @ProtoNumber(1)
            var active: Boolean,
            @ProtoNumber(2)
            var selected: Long,
            @ProtoNumber(3)
            var variants: MutableList<Entry>
        ) {
            @Serializable
            data class Entry(
                @ProtoNumber(1)
                var id: Long,
                @ProtoNumber(2)
                var default: Boolean
            )
        }
    }

    @Serializable
    data class RssState(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var dtLast: LocalDateTime,
        @ProtoNumber(3)
        var interval: Long
    )

    @Deprecated(
        message = "do not use",
        replaceWith = ReplaceWith(
            "org.ssoft.common.server.portal.User",
            "org.ssoft.common.server.portal.UPermission",
            "org.ssoft.common.server.portal.CompanyBase",
            "org.ssoft.common.server.portal.ServerBase",
            "org.ssoft.common.server.portal.Permission",
        )
    )
    @Serializable
    data class User(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(2)
        var server: ServerBase,
        @ProtoNumber(3)
        override var name: String,
        @ProtoNumber(4)
        var login: String,
        @ProtoNumber(5)
        var company: CompanyBase,
        @ProtoNumber(6)
        var permission: Permission,
        /**
         * @see org.ssoft.common.type.DashboardType
         */
        @ProtoNumber(7)
        var dashboards: List<Int> = DashboardType.all.map { it.mask },
    ) : EntryFilter

    @Deprecated(
        message = "do not use",
        replaceWith = ReplaceWith("org.ssoft.common.server.portal.ServerBase")
    )
    @Serializable
    data class ServerBase(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var id_default: Long,
        @ProtoNumber(3)
        var name: String
    )

    @Serializable
    data class Campaign(
        @ProtoNumber(1)
        override var id: Long,
        @ProtoNumber(12)
        var groupId: Long,
        @ProtoNumber(16)
        var editorVersion: Int = 1,
        @ProtoNumber(2)
        override var name: String,
        /** @see api.CampaignState */
        @ProtoNumber(3)
        var state: Int,
        @ProtoNumber(4)
        var start: LocalDateTime,
        @ProtoNumber(5)
        var end: LocalDateTime,
        @ProtoNumber(15)
        var edit: LocalDateTime,
        @ProtoNumber(6)
        var locations: MutableList<LocationItem> = mutableListOf(),
        @ProtoNumber(7)
        var visuals: MutableList<VisualGroup> = mutableListOf(),
        @ProtoNumber(8)
        var isCommercial: Boolean,
        @ProtoNumber(9)
        var isNonCommercial: Boolean,
        @ProtoNumber(10)
        var settings: CampaignSettings,
        @ProtoNumber(11)
        var impressionExpected: Long,
        @ProtoNumber(13)
        var volume: Int,
        @ProtoNumber(14)
        var weatherPictograms: List<WeatherPictograms> = listOf(),
        @ProtoNumber(17)
        var apps: MutableList<ImplPlaylist.AppConfig> = mutableListOf(),
        @ProtoNumber(18)
        var appBehavior: Int = AppBehavior.CAMPAIGN_OVERLAP.mask,
        @ProtoNumber(19)
        var otherCampaignGroups: MutableList<OtherCampaignGroup> = mutableListOf(),
        @ProtoNumber(20)
        var visualRotaryMode: Int = VisualRotaryMode.DYNAMIC_VISUALS.mask,
        @ProtoNumber(21)
        var miniApp: MutableList<MiniApp> = mutableListOf()
    ) : EntryFilter {
        companion object;

        @Serializable
        data class MiniApp(
            @ProtoNumber(1)
            var enabled: Boolean = false,
            @ProtoNumber(2)
            var dt: LocalDateTime = LocalDateTime.EMPTY,
            @ProtoNumber(3)
            var bg: Long = 0xFFFFFFFFL,
            @ProtoNumber(4)
            var fg: Long = 0xFF000000L,
            @ProtoNumber(5)
            val labels: Labels = Labels()
        ) {
            companion object;

            @Serializable
            data class Labels(
                @ProtoNumber(1)
                var days: String = "Days",
                @ProtoNumber(2)
                var hours: String = "Hours",
                @ProtoNumber(3)
                var minutes: String = "Minutes",
                @ProtoNumber(4)
                var seconds: String = "Seconds",
                @ProtoNumber(5)
                var ended: String = "Ended"
            )
        }

        @Serializable
        data class OtherCampaignGroup(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            var id_campaign: Long,
            @ProtoNumber(3)
            override var name: String,
            @ProtoNumber(4)
            var exclude: Boolean,
            @ProtoNumber(5)
            var res: Res = Res(),
            @ProtoNumber(6)
            /** @see CampaignState **/
            var state: Int
        ) : EntryFilter {
            @Serializable
            data class Res(
                @ProtoNumber(1)
                var id: Long = -1,
                @ProtoNumber(2)
                /**
                 * @see ScreenRatio
                 */
                var ratio: Int = ScreenRatio.NONE.mask,
            )
        }

        @Serializable
        data class WeatherPictograms(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(3)
            override var name: String,
            @ProtoNumber(2)
            var ordering: Int,
        ) : EntryFilter

        @Serializable
        data class CampaignSettings(
            @ProtoNumber(1)
            var companyCount: Int,
            @ProtoNumber(2)
            var companyDuration: Long,
            @ProtoNumber(3)
            var campaignCount: Int,
            @ProtoNumber(4)
            var campaignDuration: Long,
            @ProtoNumber(5)
            /** @see api.CampaignType */
            var type: Int,
            @ProtoNumber(6)
            var campaignVisualIgnoreRatio: Boolean,
            @ProtoNumber(7)
            var campaignVisualIgnoreDuration: Boolean,
            @ProtoNumber(8)
            @Deprecated("DO NOT USE")
            var campaignVisualIgnoreOrientation: Boolean = false,
            @ProtoNumber(9)
            var enableCommercial: Boolean,
            @ProtoNumber(10)
            var enableCommercialAsNonCommercial: Boolean,
        ) {
            companion object;
        }

        @Serializable
        data class LocationItem(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var devices: MutableList<DeviceItem> = mutableListOf(),
            @ProtoNumber(4)
            var days: List<Day> = Day.defaultWeek
        ) : EntryFilter {
            companion object;
        }

        @Serializable
        data class Day(
            @ProtoNumber(1)
            var day: Int,
            @ProtoNumber(2)
            var start: LocalTime,
            @ProtoNumber(3)
            var end: LocalTime,
        ) {
            companion object {
                val defaultWeek by lazy {
                    ImplLocation.Day.emptyWeek()
                        .map {
                            Day(
                                day = it.day,
                                start = it.start,
                                end = it.end
                            )
                        }
                }
            }
        }

        @Serializable
        data class DeviceItem(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(5)
            var isHorizontal: Boolean,
            @ProtoNumber(6)
            var isActive: Boolean,
            @ProtoNumber(7)
            /**
             * @see ScreenRatio
             */
            var screenRatio: Int,
            @ProtoNumber(8)
            var excludedDoY: List<Int> = listOf()
        ) : EntryFilter {
            companion object;
        }

        @Serializable
        data class VisualGroup(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String = id.toString(),
            @ProtoNumber(3)
            var isPrimary: Boolean,
            @ProtoNumber(7)
            var isCustom: Boolean = false,
            @ProtoNumber(8)
            var res: MutableList<ResItem> = mutableListOf(),
            @ProtoNumber(10)
            var conditions: ImplPlaylist.Conditions = ImplPlaylist.Conditions(
                days = DayOfWeek.allDaysIndex,
                time = ImplPlaylist.Conditions.Time(
                    start = LocalTime.MIN,
                    end = LocalTime.MAX
                )
            )
        ) : EntryFilter {
            companion object {
                val EMPTY
                    get() = VisualGroup(
                        id = GenId.get(),
                        name = "",
                        isPrimary = false,
                        isCustom = true
                    )
            }
        }

        @Serializable
        data class ResItem(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            var conditionId: Long,
            @ProtoNumber(3)
            var resourceId: Long,
            @ProtoNumber(4)
            override var name: String = id.toString(),
            @ProtoNumber(5)
            var duration: Long,
            @ProtoNumber(6)
            var type: Int,
            @Deprecated("do not use")
            @ProtoNumber(8)
            var aspectRatio: Int = AspectRatio.NONE.mask,
            @ProtoNumber(9)
            var isHorizontal: Boolean,
            @ProtoNumber(10)
            var width: Int,
            @ProtoNumber(11)
            var height: Int,
            @ProtoNumber(12)
            var isCustom: Boolean = false,
            /**
             * @see ScreenRatio
             */
            @ProtoNumber(13)
            var screenRatio: Int = ScreenRatio.NONE.mask
        ) : EntryFilter {
            companion object;
        }
    }

    @Deprecated(
        message = "do not use",
        replaceWith = ReplaceWith(
            "org.ssoft.common.server.portal.Permission",
            "org.ssoft.common.server.portal.UPermission"
        )
    )
    @Serializable
    data class Permission(
        @ProtoNumber(1)
        var company: Int,
        @ProtoNumber(2)
        var user: Int,
        @ProtoNumber(3)
        var location: Int,
        @ProtoNumber(4)
        var device_info: Int,
        @ProtoNumber(5)
        var device_display: Int,
        @ProtoNumber(6)
        var user_permission: Int,
        @ProtoNumber(7)
        var device_layer_bottom: Int,
        @ProtoNumber(8)
        var server: Int,
        @ProtoNumber(9)
        var device_actions: Int,
        @ProtoNumber(10)
        var resource: Int,
        @ProtoNumber(11)
        var device_layer_top: Int,
        @ProtoNumber(12)
        var device_share: Int,
        @ProtoNumber(13)
        var translation: Int,
        @ProtoNumber(14)
        var report_system: Int,
        @ProtoNumber(15)
        var report_layer_top: Int,
        @ProtoNumber(16)
        var report_layer_bottom: Int,
        @ProtoNumber(17)
        var location_emergency: Int,
        @ProtoNumber(18)
        var license: Int = NONE,
        @ProtoNumber(19)
        var hardware: Int = OWN_VIEW
    ) {

        fun canCompany(@UPermission mask: Int): Boolean = company and mask > 0
        fun canUser(@UPermission mask: Int): Boolean = user and mask > 0
        fun canUserPermission(@UPermission mask: Int): Boolean = user_permission and mask > 0
        fun canLocation(@UPermission mask: Int): Boolean = location and mask > 0
        fun canDeviceDisplay(@UPermission mask: Int): Boolean = device_display and mask > 0
        fun canDeviceLayerBottom(@UPermission mask: Int): Boolean = device_layer_bottom and mask > 0
        fun canDeviceInfo(@UPermission mask: Int): Boolean = device_info and mask > 0
        fun canServer(@UPermission mask: Int): Boolean = server and mask > 0
        fun canDeviceAction(@UPermission mask: Int): Boolean = device_actions and mask > 0
        fun canResource(@UPermission mask: Int): Boolean = resource and mask > 0
        fun canDeviceLayerTop(@UPermission mask: Int): Boolean = device_layer_top and mask > 0

        @Suppress("unused")
        fun canDeviceShare(@UPermission mask: Int): Boolean = device_share and mask > 0
        fun canTranslation(@UPermission mask: Int): Boolean = translation and mask > 0

        @Suppress("unused")
        fun canReportSystem(@UPermission mask: Int): Boolean = report_system and mask > 0
        fun canReportLayerTop(@UPermission mask: Int): Boolean = report_layer_top and mask > 0

        @Suppress("unused")
        fun canReportLayerBottom(@UPermission mask: Int): Boolean = report_layer_bottom and mask > 0
        fun canLocationEmergency(@UPermission mask: Int): Boolean = location_emergency and mask > 0
        fun canLicense(@UPermission mask: Int): Boolean = license and mask > 0
        fun canHardware(@UPermission mask: Int): Boolean = hardware and mask > 0
        fun isSuperAdmin(): Boolean = canServer(OTHERS_ADD)
        fun isServerAdmin(): Boolean = canCompany(OTHERS_ADD)

        companion object {

            @Suppress("unused")
            @UPermission
            const val NONE = 1

            @UPermission
            const val OWN_VIEW = 4

            @UPermission
            const val OWN_ADD = 8

            @UPermission
            const val OWN_EDIT = 16

            @UPermission
            const val OWN_DELETE = 32

            @UPermission
            const val OTHERS_VIEW = 128

            @UPermission
            const val OTHERS_ADD = 256

            @UPermission
            const val OTHERS_EDIT = 512

            @UPermission
            const val OTHERS_DELETE = 1024

            val own = arrayOf(
                OWN_VIEW, OWN_ADD, OWN_EDIT, OWN_DELETE
            )
            val others = arrayOf(
                OTHERS_VIEW, OTHERS_ADD, OTHERS_EDIT, OTHERS_DELETE
            )
            val all = own + others
        }
    }

    object Filter {
        @Serializable
        data class DateRange(
            @ProtoNumber(1)
            val start: LocalDate = LocalDate.EMPTY,
            @ProtoNumber(2)
            val end: LocalDate = LocalDate.EMPTY
        )

        @Serializable
        data class CampaignListing(
            @ProtoNumber(1)
            val states: Int = CampaignState.all.sumOf { it.state } - CampaignState.DELETED.state,
            @ProtoNumber(2)
            val excludedLocations: List<Long> = listOf(),
            @ProtoNumber(3)
            val dtRange: DateRange? = null,
            @ProtoNumber(4)
            val mode: Int = MODE_ALL
        ) {
            companion object {
                const val MODE_NC = 2
                const val MODE_C = 4
                const val MODE_C_NC = 8

                const val MODE_ALL = MODE_C + MODE_NC + MODE_C_NC
            }
        }
    }

    @Serializable
    data class ReportCampaign(
        @ProtoNumber(1)
        var id: Long,
        @ProtoNumber(2)
        var companyId: Long,
        @ProtoNumber(3)
        var actualCampaignId: Long,
        @ProtoNumber(4)
        var campaign: List<Campaign> = listOf(),
        @ProtoNumber(5)
        var counter: Counter = Counter(id),
        @ProtoNumber(6)
        var locations: List<LocationItem> = mutableListOf(),
    ) {
        companion object;

        @Serializable
        data class Campaign(
            @ProtoNumber(1)
            var id: Long,
            /** @see api.CampaignState */
            @ProtoNumber(2)
            var state: Int,
            @ProtoNumber(3)
            var name: String,

            @ProtoNumber(4)
            var start: LocalDateTime,
            @ProtoNumber(5)
            var end: LocalDateTime,
            @ProtoNumber(6)
            var edit: LocalDateTime,
            @ProtoNumber(7)
            var stop: LocalDateTime,

            @ProtoNumber(8)
            var isCommercial: Boolean,
            @ProtoNumber(9)
            var isNonCommercial: Boolean,
            @ProtoNumber(10)
            var impressionExpected: Long,

            @ProtoNumber(11)
            var settings: CampaignSettings,

            @ProtoNumber(12)
            var devices: List<DeviceItem> = mutableListOf(),
            @ProtoNumber(13)
            var visuals: List<VisualItem> = mutableListOf(),
        )

        @Serializable
        data class Counter(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var devices: List<CounterEntry> = listOf(),
            @ProtoNumber(3)
            var resource: List<CounterEntry> = listOf()
        )

        @Serializable
        data class CounterEntry(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            @Deprecated("DO NOT USE")
            var campaignId: Long = -1,
            @ProtoNumber(3)
            var counter: Long
        )

        @Serializable
        data class CampaignSettings(
            @ProtoNumber(1)
            var count: Int,
            @ProtoNumber(2)
            var duration: Long,
            @ProtoNumber(3)
            var type: Int,
        ) {
            companion object;
        }

        @Serializable
        data class DeviceItem(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String,
            @ProtoNumber(3)
            var id_location: Long = -1,
        )

        @Serializable
        data class LocationItem(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var name: String,
            @ProtoNumber(3)
            var oh: List<ImplLocation.Day> = ImplLocation.Day.emptyWeek()
        )

        @Serializable
        data class VisualItem(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(2)
            var isPrimary: Boolean,
            @ProtoNumber(3)
            var days: Int,
            @ProtoNumber(4)
            var start: LocalTime,
            @ProtoNumber(5)
            var end: LocalTime,
            @ProtoNumber(6)
            var resources: List<ResourceItem> = listOf()
        )

        @Serializable
        data class ResourceItem(
            @ProtoNumber(1)
            var id: Long,
            @ProtoNumber(3)
            var duration: Long,
            @ProtoNumber(4)
            var type: Int,
            @ProtoNumber(5)
            var isHorizontal: Boolean,
            @ProtoNumber(6)
            var width: Int,
            @ProtoNumber(7)
            var height: Int
        )
    }

    @Serializable
    data class RssInstance(
        @ProtoNumber(1)
        var channels: MutableList<Channel> = mutableListOf(),
        @ProtoNumber(2)
        var item: Item = Item()
    ) {
        @Serializable
        data class Item(
            @ProtoNumber(17)
            var id: Long = -1L,
            @ProtoNumber(18)
            var name: String = "",
            @ProtoNumber(19)
            var description: String = "",
            @ProtoNumber(20)
            var logo: Long = -8L,
            @ProtoNumber(21)
            var canDelete: Boolean = false,

            @ProtoNumber(1)
            var channelId: Long = -1,
            @ProtoNumber(2)
            var labelSearch: String = "",
            @ProtoNumber(3)
            var labelCategory: String = "",
            @ProtoNumber(4)
            var labelItemDescription: String = "",
            @ProtoNumber(5)
            var labelItemCategory: String = "",
            @ProtoNumber(6)
            var labelItemAuthor: String = "",
            @ProtoNumber(7)
            var labelItemDetails: String = "",
            @ProtoNumber(8)
            var labelItemPublished: String = "",
            @ProtoNumber(9)
            var labelItemValidUntil: String = "",
            @ProtoNumber(10)
            var labelItemAttachments: String = "",
            @ProtoNumber(11)
            var showDetail: Boolean = true,
            @ProtoNumber(12)
            var showListingDescription: Boolean = false,
            @ProtoNumber(13)
            var showListingImage: Boolean = true,

            @ProtoNumber(14)
            var showListingTitle: Boolean = true,
            @ProtoNumber(15)
            var showListingDescriptionAsHtml: Boolean = false,
            @ProtoNumber(16)
            var showItemDescriptionAsHtml: Boolean = true,
        )

        @Serializable
        data class Channel(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var url: String,
        ) : EntryFilter
    }

    @Serializable
    data class ServerLicensing(
        /**
         * @see api.CurrencyType
         */
        @ProtoNumber(1)
        var currency: Int,
        @ProtoNumber(2)
        var percentage: Int,
        @ProtoNumber(3)
        var items: MutableList<Company> = mutableListOf()
    ) {
        companion object;

        @Serializable
        data class Company(
            @ProtoNumber(1)
            override var id: Long,
            @ProtoNumber(2)
            override var name: String,
            @ProtoNumber(3)
            var countDevice: Long,
            @ProtoNumber(4)
            var licenseActive: License = License(),
            @ProtoNumber(5)
            var licensePending: License = License(),
        ) : EntryFilter
    }

    @Serializable
    data class CompanyLicenseHistory(
        @ProtoNumber(1)
        var name: String,
        /**
         * @see api.CurrencyType
         */
        @ProtoNumber(2)
        var currency: Int,
        @ProtoNumber(3)
        var items: MutableList<License> = mutableListOf(),
        @ProtoNumber(4)
        var primaryCompany: Boolean = false
    ) {
        companion object;
    }

    @Serializable
    data class License(
        @ProtoNumber(1)
        var id: Long = -1L,
        /**
         * @see api.LicenseState
         */
        @ProtoNumber(2)
        var state: Int = LicenseState.IDLE.mask,
        /**
         * raw resource of document
         */
        @ProtoNumber(3)
        var doc: Long = -1L,
        @ProtoNumber(4)
        var msg: String = "",
        @ProtoNumber(5)
        var price: Int = 20,
        @ProtoNumber(6)
        var upload: LocalDate = LocalDate.EMPTY,
        @ProtoNumber(7)
        var start: LocalDate = LocalDate.EMPTY,
        @ProtoNumber(8)
        var end: LocalDate = LocalDate.EMPTY,
        @ProtoNumber(9)
        var reject: LocalDate = LocalDate.EMPTY,
        @ProtoNumber(10)
        var approved: LocalDate = LocalDate.EMPTY,
    )

    @Serializable
    data class WheelHistoryOverview(
        var items: List<Entry> = listOf()
    ) {
        @Serializable
        data class Entry(
            val item: App.Wheel.Item,
            val dt: LocalDateTime,
            val code: String
        )
    }
}

